import { Placement } from '@floating-ui/react';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Locales } from 'shared/assets/localization';
import { PopupMenu } from 'shared/components/ui/popup-menu';
import { NLP } from 'shared/constants';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useNlpRegex } from 'shared/hooks/use-nlp-regex';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';
import { NewEntityTypes, Person, SuggestionTriggers } from 'shared/types/nlp';
import { NewTagWithID, Tag } from 'shared/types/tag';
import { getIsMacOs } from 'shared/utils/get-is-mac-os';
import { isNumber } from 'shared/utils/is-number';
import { WordId } from 'shared/utils/nlp/get-ids-for-words';
import { escapeRegex } from 'shared/utils/nlp/inside-ignored-range';
import { useTheme } from 'styled-components';

import { getCursorPosition } from './get-cursor-position';
import * as Styled from './get-styles';
import { getStyles } from './get-styles';
import { renderHighlightedText } from './highlighting-logic';
import { useWordIdList } from './use-word-id-list';

type Suggestion = {
  id: ID | null;
  name: string;
  type: SuggestionTriggers;
  extraInfo?: { isCreate?: boolean; isPremium?: boolean };
};
const autoCompletePositions: Placement[] = [
  'bottom-start',
  'top-start',
  'right-start',
  'left-start',
];

const premiumLabel: Record<Locales, string> = {
  [Locales.EnUS]: 'Buy Griply Premium to create more goals',
  [Locales.NlNL]: 'Koop Griply Premium om meer doelen aan te maken',
  [Locales.DeDE]: 'Kaufe Griply Premium, um mehr Ziele zu erstellen',
  [Locales.EsES]: 'Compra Griply Premium para crear más metas',
};

export type NLPInputProps = {
  value: string;
  goals: (Goal | NewGoalWithID)[];
  lifeAreas: LifeArea[];
  tags: (Tag | NewTagWithID)[];
  people?: Person[];
  language: Locales;
  dateFormat: DateFormatOptions;
  createTargetHabitAllowed: boolean;
  createHabitAllowed: boolean;
  createGoalAllowed: boolean;
  placeholder?: string;

  onChange: (
    value: string,
    extraInfo: { wordIds: WordId[]; ignoredWordIds: ID[] },
  ) => void;
  onSubmit: (
    value: string,
    extraInfo: { wordIds: WordId[]; ignoredWordIds: ID[] },
  ) => void;
  onPremium: () => void;
  onBulkPaste: (lines: string[]) => void;
  onNewEntity: (type: NewEntityTypes, value: string) => void;
};

export const NLPInput = forwardRef<HTMLDivElement, NLPInputProps>(
  (
    {
      value,
      goals,
      lifeAreas,
      tags,
      people,
      language,
      dateFormat,
      createTargetHabitAllowed,
      createHabitAllowed,
      createGoalAllowed,
      placeholder,

      onChange,
      onSubmit,
      onPremium,
      onBulkPaste,
      onNewEntity,
    },
    ref,
  ) => {
    const isComposing = useRef(false);
    const theme = useTheme();
    const styles = useMemo(() => getStyles(theme), [theme]);
    // The MASTER_REGEX_G is the core of the keyword detection system. It combines multiple patterns into one large regex.
    // Each part is a capturing group, allowing us to identify which type of keyword was matched.
    const MASTER_REGEX_G = useNlpRegex({ language, people });
    const [isComposingState, setIsComposingState] = useState(false);

    // --- State Management ---
    const wordIds = useWordIdList(value, MASTER_REGEX_G);
    const [ignoredWordIds, setIgnoredWordIds] = useState<ID[]>([]);

    // State for managing the autocomplete dropdown (visibility, suggestions, etc.).
    const [autocomplete, setAutocomplete] = useState<{
      show: boolean;
      suggestions: Suggestion[];
      trigger: SuggestionTriggers | null;
      startIndex: number;
      textToReplace: string;
      cursorIndex?: number;
    }>({
      show: false,
      suggestions: [],
      trigger: null,
      startIndex: -1,
      textToReplace: '',
    });
    // The index of the currently highlighted item in the autocomplete dropdown.
    const [activeIndex, setActiveIndex] = useState(-1);

    // --- Refs ---
    // Ref to the main input wrapper for detecting outside clicks to close autocomplete.
    const wrapperRef = useRef<HTMLDivElement>(null);
    // Ref to the content-editable div used as the main input, allowing direct DOM manipulation.
    const inputRef = useForwardedRef(ref);
    // Ref to store an array of DOM nodes for each autocomplete suggestion, for scrolling active items into view.
    const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);
    // Ref to store the desired cursor position after the input is programmatically updated, ensuring a smooth user experience.
    const desiredCursorPos = useRef<number | null>(null);

    // --- Memoized Regular Expressions ---
    // These regexes are memoized with useMemo so they are only recompiled
    const TAG_REGEX_G = useMemo(
      () =>
        tags.length
          ? new RegExp(
              `#(${tags.map(({ name }) => escapeRegex(name)).join('|')})`,
              'g',
            )
          : undefined,
      [tags],
    );
    const PERSON_REGEX_G = useMemo(
      () =>
        people?.length
          ? new RegExp(
              `@(${people.map(({ name }) => escapeRegex(name)).join('|')})`,
              'g',
            )
          : undefined,
      [people],
    );

    // --- State Refs for Native Listener ---
    const autocompleteRef = useRef(autocomplete);
    const activeIndexRef = useRef(activeIndex);
    const autocompleteSuggestionsRef = useRef<Suggestion[]>([]);

    // Update refs whenever state changes
    useEffect(() => {
      autocompleteRef.current = autocomplete;
      activeIndexRef.current = activeIndex;
    }, [autocomplete, activeIndex]);

    // --- Effects ---
    // Effect to handle clicks outside the input/autocomplete component to close the dropdown.
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setAutocomplete({
            show: false,
            suggestions: [],
            trigger: null,
            startIndex: -1,
            textToReplace: '',
          });
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [wrapperRef]);

    // Effect to scroll the active item in the autocomplete dropdown into view when arrow keys are used.
    useEffect(() => {
      if (activeIndex >= 0 && suggestionRefs.current[activeIndex]) {
        suggestionRefs.current[activeIndex]?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }, [activeIndex]);

    useEffect(() => {
      onChange(value, {
        wordIds: [...(wordIds.current ?? [])],
        ignoredWordIds,
      });
    }, [ignoredWordIds, onChange, value, wordIds]);

    // Trap arrow keys at the native level to prevent headless-tree from hijacking navigation
    useEffect(() => {
      const inputEl = inputRef.current;
      if (!inputEl) {
        return;
      }

      const handleNativeKeyDown = (e: KeyboardEvent) => {
        if (['ArrowDown', 'ArrowUp'].includes(e.key)) {
          // Always stop propagation to prevent vtree navigation
          e.stopPropagation();
          e.stopImmediatePropagation();

          // If menu is open, handle navigation and prevent cursor move
          if (autocompleteRef.current.show) {
            e.preventDefault();
            const suggestions = autocompleteSuggestionsRef.current;
            const currentActive = activeIndexRef.current;

            if (e.key === 'ArrowDown') {
              setActiveIndex(
                currentActive < suggestions.length - 1 ? currentActive + 1 : 0,
              );
            } else {
              setActiveIndex(
                currentActive > 0 ? currentActive - 1 : suggestions.length - 1,
              );
            }
          }
        } else if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
          // Just stop propagation to protect vtree, but allow cursor move
          e.stopPropagation();
          e.stopImmediatePropagation();
        }
      };

      inputEl.addEventListener('keydown', handleNativeKeyDown);
      return () => {
        inputEl.removeEventListener('keydown', handleNativeKeyDown);
      };
    }, [inputRef, autocomplete.show]);

    // --- Event Handlers & Core Functions ---

    // Handles every change in the input field. It updates the state and triggers autocomplete logic.
    const handleInputChange = (e: React.FormEvent<HTMLDivElement>) => {
      const target = e.target as HTMLDivElement;
      desiredCursorPos.current = getCursorPosition(target);
      let text = target.innerHTML
        // Replace non-breaking spaces (&nbsp;) with a standard space.
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/<[^>]+>/g, '')
        // Convert full-width IME characters to standard half-width ones.
        .replace(/～/g, '~')
        .replace(/＃/g, '#')
        .replace(/＠/g, '@');

      if (text.includes('->')) {
        const arrowRegex = /->/g;
        let match;
        let adjustment = 0;
        const currentPos = desiredCursorPos.current ?? 0;

        while ((match = arrowRegex.exec(text)) !== null) {
          if (match.index < currentPos) {
            adjustment += 1;
          }
        }
        text = text.replace(/->/g, '→');
        if (desiredCursorPos.current !== null) {
          desiredCursorPos.current -= adjustment;
        }
      }

      onChange(text, { wordIds: [...(wordIds.current ?? [])], ignoredWordIds });
      setActiveIndex(-1);

      const cursorPosition = desiredCursorPos.current ?? 0;
      const textBeforeCursor = text.substring(0, cursorPosition);

      // 1. Find the last potential trigger character ('~', '#', '@') before the cursor.
      const lastTildeIndex = textBeforeCursor.lastIndexOf('~');
      const lastHashIndex = textBeforeCursor.lastIndexOf('#');
      const lastAtIndex = textBeforeCursor.lastIndexOf('@');

      // 2. Determine which of these triggers is the most recent one.
      const potentialTriggers = [
        { char: '~', index: lastTildeIndex },
        { char: '#', index: lastHashIndex },
        { char: '@', index: lastAtIndex },
      ]
        .filter((t) => t.index > -1)
        .sort((a, b) => b.index - a.index);

      let activeTrigger = null;
      if (potentialTriggers.length) {
        const lastTrigger = potentialTriggers[0];

        // 3. A trigger is considered "active" for autocomplete if:
        //    a) It's at the start of the input OR it's preceded by a space or our terminator character.
        //    b) There is no terminator character *between* the trigger and the cursor. This means we're not editing a completed "pill".
        const charBefore =
          lastTrigger.index > 0 ? text.charAt(lastTrigger.index - 1) : ' ';
        const textBetweenTriggerAndCursor = textBeforeCursor.substring(
          lastTrigger.index,
        );

        if (
          ([' ', NLP.pillSeparator, NLP.whitespace].includes(charBefore) ||
            lastTrigger.index === 0) &&
          !textBetweenTriggerAndCursor.includes(NLP.whitespace)
        ) {
          activeTrigger = lastTrigger;
        }
      }

      if (activeTrigger) {
        const { char: trigger, index: triggerIndex } = activeTrigger;

        const searchTerm = textBeforeCursor.substring(triggerIndex + 1);

        // If the character immediately after the trigger is a space,
        // treat it as plain text and do not show autocomplete.
        if (
          searchTerm.startsWith(' ') ||
          searchTerm.startsWith(NLP.pillSeparator)
        ) {
          setAutocomplete({
            show: false,
            suggestions: [],
            trigger: null,
            startIndex: -1,
            textToReplace: '',
          });
          return;
        }

        // 4. Find the full extent of the property being typed (for replacement purposes).
        const textFromTrigger = text.substring(triggerIndex);
        let propertyPattern;
        if (trigger === SuggestionTriggers.LinkedItem) {
          propertyPattern = new RegExp(
            `^${SuggestionTriggers.LinkedItem}${NLP.goalSubPattern}`,
            'u',
          );
        } else if (trigger === SuggestionTriggers.Tag) {
          propertyPattern = new RegExp(
            `^${SuggestionTriggers.Tag}${NLP.tagSubPattern}`,
            'u',
          );
        } else if (trigger === SuggestionTriggers.Person) {
          propertyPattern = new RegExp(
            `^${SuggestionTriggers.Person}${NLP.personSubPattern}`,
            'u',
          );
        }

        const match = propertyPattern
          ? textFromTrigger.match(propertyPattern)
          : null;
        const textToReplace = match ? match[0] : trigger;

        const triggerTypeMap: Record<SuggestionTriggers, NewEntityTypes> = {
          [SuggestionTriggers.LinkedItem]: NewEntityTypes.Goal,
          [SuggestionTriggers.Tag]: NewEntityTypes.Tag,
          [SuggestionTriggers.Person]: NewEntityTypes.Person,
        };

        // 5. Filter suggestions based on the search term.
        const triggerType =
          triggerTypeMap[trigger as SuggestionTriggers] ?? null;
        let filtered: Suggestion[] = [];
        let sourceList: Suggestion[] = [];

        if (trigger === SuggestionTriggers.LinkedItem) {
          sourceList = [...goals, ...lifeAreas].map(({ id, name }) => ({
            id,
            name,
            type: SuggestionTriggers.LinkedItem,
          }));
          const searchKeywords = searchTerm
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);
          if (searchKeywords.length) {
            filtered = sourceList.filter((g) => {
              const lowerG = g.name.toLowerCase();
              return searchKeywords.every((kw) => lowerG.includes(kw));
            });
          } else {
            filtered = sourceList; // Show all if search term is empty/whitespace
          }
        } else if (trigger === SuggestionTriggers.Tag && TAG_REGEX_G) {
          const allTagMatches = Array.from(text.matchAll(TAG_REGEX_G));
          const existingTagsToFilter = allTagMatches
            .filter((m) => m.index !== triggerIndex)
            .map((m) => m[1]);
          sourceList = tags.reduce<Suggestion[]>((acc, { id, name }) => {
            if (!existingTagsToFilter.includes(name)) {
              acc.push({ id, name, type: SuggestionTriggers.Tag });
            }
            return acc;
          }, []);
          const searchKeywords = searchTerm
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);
          if (searchKeywords.length) {
            filtered = sourceList.filter((t) => {
              const lowerT = t.name.toLowerCase();
              return searchKeywords.every((kw) => lowerT.includes(kw));
            });
          } else {
            filtered = sourceList;
          }
        } else if (trigger === SuggestionTriggers.Person && PERSON_REGEX_G) {
          const allPersonMatches = Array.from(text.matchAll(PERSON_REGEX_G));
          const existingPeopleToFilter = allPersonMatches
            .filter((m) => m.index !== triggerIndex)
            .map((m) => m[1]);
          sourceList =
            people?.reduce<Suggestion[]>((acc, { name }) => {
              if (!existingPeopleToFilter.includes(name)) {
                acc.push({ id: null, name, type: SuggestionTriggers.Person });
              }
              return acc;
            }, []) ?? [];
          const searchKeywords = searchTerm
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);
          if (searchKeywords.length) {
            filtered = sourceList.filter((p) => {
              const lowerP = p.name.toLowerCase();
              return searchKeywords.every((kw) => lowerP.includes(kw));
            });
          } else {
            filtered = sourceList;
          }
        }

        const finalSearchTerm = searchTerm.trim();
        if (finalSearchTerm.length && triggerType) {
          const exactMatch = sourceList.some(
            (item) => item.name.toLowerCase() === finalSearchTerm.toLowerCase(),
          );
          if (!exactMatch) {
            filtered.push(
              triggerType !== NewEntityTypes.Goal || createGoalAllowed
                ? {
                    id: null,
                    name: `Create ${triggerType} "${finalSearchTerm}"`,
                    type:
                      triggerType === NewEntityTypes.Person
                        ? SuggestionTriggers.Person
                        : triggerType === NewEntityTypes.Goal
                          ? SuggestionTriggers.LinkedItem
                          : SuggestionTriggers.Tag,
                    extraInfo: { isCreate: true },
                  }
                : {
                    id: null,
                    name: premiumLabel[language],
                    type: SuggestionTriggers.LinkedItem,
                    extraInfo: { isPremium: true },
                  },
            );
          }
        }

        // 6. Update the autocomplete state with the suggestions.
        setAutocomplete({
          show: !!filtered.length,
          suggestions: filtered,
          trigger: trigger as SuggestionTriggers,
          startIndex: triggerIndex,
          textToReplace,
          cursorIndex: cursorPosition,
        });
        setActiveIndex(filtered.length ? 0 : -1);
      } else {
        // 7. If no active trigger is found, ensure the autocomplete is closed.
        setAutocomplete({
          show: false,
          suggestions: [],
          trigger: null,
          startIndex: -1,
          textToReplace: '',
        });
      }
    };

    const handleCompositionStart = () => {
      isComposing.current = true;
      setIsComposingState(true);
    };

    const handleCompositionEnd = (
      e: React.CompositionEvent<HTMLDivElement>,
    ) => {
      isComposing.current = false;
      setIsComposingState(false);
      // Manually trigger the input change handler after composition ends
      // to ensure the state is updated with the final composed text.
      handleInputChange(e);
    };

    // Handles selecting an item from the autocomplete dropdown.
    const handleSelection = (selection?: Suggestion) => {
      if (!selection) {
        return;
      }
      // if premium, call onPremium
      if (selection?.extraInfo?.isPremium) {
        onPremium();
        setAutocomplete({
          show: false,
          suggestions: [],
          trigger: null,
          startIndex: -1,
          textToReplace: '',
        });
        setActiveIndex(-1);
        return;
      }

      let suggestion = selection;

      // If "Create new..." was selected, add the new item to the appropriate list.
      if (selection?.extraInfo?.isCreate) {
        const createRegex = new RegExp(
          `Create (${Object.values(NewEntityTypes).join('|')}) "(.+)"`,
        );
        const createMatch = selection.name.match(createRegex);

        if (createMatch) {
          const entityType =
            selection.type === SuggestionTriggers.Person
              ? NewEntityTypes.Person
              : selection.type === SuggestionTriggers.LinkedItem
                ? NewEntityTypes.Goal
                : NewEntityTypes.Tag;
          const value = createMatch[2];
          suggestion = { id: null, name: value, type: selection.type };
          onNewEntity(entityType, value);
        }
      }

      // Update the input value with the selected suggestion.
      const prefix = value.substring(0, autocomplete.startIndex);

      // Calculate the suffix based on the length of the text that was being typed for autocomplete.
      const suffix = isNumber(autocomplete.cursorIndex)
        ? value.substring(autocomplete.cursorIndex)
        : '';

      // FIX: A zero-width space (\u200B) is added as a "terminator" to prevent the greedy tag/goal/person regex
      // from consuming subsequent text. A regular space is added after for a normal typing experience.
      const suggestionText = `${autocomplete.trigger}${suggestion.name}${NLP.whitespace} `;
      const newValue = `${prefix}${suggestionText}${suffix.trimStart()}`;
      desiredCursorPos.current = (prefix + suggestionText).length;
      onChange(newValue, {
        wordIds: [...(wordIds.current ?? [])],
        ignoredWordIds,
      });
      setAutocomplete({
        show: false,
        suggestions: [],
        trigger: null,
        startIndex: -1,
        textToReplace: '',
      });
      setActiveIndex(-1);
    };

    const handleSelectionMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const autocompleteSuggestions = useMemo(() => {
      if (!autocomplete.show) {
        return [];
      }
      if (
        autocomplete.trigger === SuggestionTriggers.Person &&
        !people?.length
      ) {
        return [];
      }
      return autocomplete.suggestions.filter(({ name, type }) => {
        const isCreate = name.startsWith('Create');
        return !(
          type === SuggestionTriggers.LinkedItem &&
          isCreate &&
          !createGoalAllowed
        );
      });
    }, [
      autocomplete.show,
      autocomplete.suggestions,
      autocomplete.trigger,
      createGoalAllowed,
      people?.length,
    ]);

    // Keep suggestions ref in sync for native listener
    useEffect(() => {
      autocompleteSuggestionsRef.current = autocompleteSuggestions;
    }, [autocompleteSuggestions]);

    // Handles keyboard navigation (arrows, Enter, Escape) for the input and autocomplete.
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.nativeEvent.isComposing || isComposing.current) {
        return;
      }

      if (autocomplete.show) {
        if (e.key === 'Enter' || e.key === 'Tab') {
          if (activeIndex >= 0) {
            e.preventDefault();
            handleSelection(autocompleteSuggestions[activeIndex]);
          } else {
            onSubmit(value, {
              wordIds: [...(wordIds.current ?? [])],
              ignoredWordIds,
            });
          }
        } else if (e.key === 'Escape') {
          e.stopPropagation();
          setAutocomplete({
            show: false,
            suggestions: [],
            trigger: null,
            startIndex: -1,
            textToReplace: '',
          });
        }
      } else if (e.key === 'Enter') {
        const isShortcut = getIsMacOs() ? e.metaKey : e.ctrlKey;

        if (isShortcut) {
          e.preventDefault();
          e.stopPropagation();
          onSubmit(value, {
            wordIds: [...(wordIds.current ?? [])],
            ignoredWordIds,
          });
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        onSubmit(value, {
          wordIds: [...(wordIds.current ?? [])],
          ignoredWordIds,
        });
      } else if (e.key === 'Backspace') {
        const selection = window.getSelection();
        if (
          selection &&
          selection.type === 'Range' &&
          selection.toString().length
        ) {
          // If text is selected, let the browser handle the backspace.
          return;
        }
        // Special backspace logic to delete entire goal/tag/person "pills" at once.
        const cursorPosition = getCursorPosition(inputRef.current!);
        if (cursorPosition === 0) {
          return;
        }

        const textBeforeCursor = value.substring(0, cursorPosition);
        const matches = [...textBeforeCursor.matchAll(MASTER_REGEX_G)];
        const lastMatch = matches[matches.length - 1];

        if (lastMatch && (lastMatch[1] || lastMatch[2] || lastMatch[3])) {
          // Only for goals, tags, and people
          const matchEndIndex = lastMatch.index! + lastMatch[0].length;
          const spaceEndIndex = matchEndIndex + 1;

          // Check for either a regular space (32) or a non-breaking space (160).
          const charCodeAtEnd = value.charCodeAt(matchEndIndex);
          if (
            cursorPosition === matchEndIndex ||
            (cursorPosition === spaceEndIndex &&
              (charCodeAtEnd === 32 || charCodeAtEnd === 160))
          ) {
            e.preventDefault();
            const newValue =
              value.substring(0, lastMatch.index) +
              value.substring(spaceEndIndex);
            desiredCursorPos.current = lastMatch.index ?? null;
            onChange(newValue, {
              wordIds: [...(wordIds.current ?? [])],
              ignoredWordIds,
            });
            return;
          }
        }
      }
    };

    // Intercepts the paste event on the input field.
    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text/plain');
      // Split the pasted text by newlines to detect multiple items.
      const lines = pastedText
        .split('\n')
        .map((line) => {
          const trimmedLine = line.trim();
          // Also removes common list markers like "- " from the start of lines.
          if (trimmedLine.startsWith('- ')) {
            return trimmedLine.substring(2);
          }
          return trimmedLine;
        })
        .filter((line) => line.length);

      if (lines.length > 1) {
        // If there are multiple lines, prepare them for the bulk-add dialog.
        onBulkPaste(lines);
      } else {
        // If it's a single line, paste it normally into the input field.
        const currentText = inputRef.current?.innerText || '';
        const cursorPos = getCursorPosition(inputRef.current!);
        const newText =
          currentText.slice(0, cursorPos) +
          pastedText +
          currentText.slice(cursorPos);
        desiredCursorPos.current = cursorPos + pastedText.length;
        onChange(newText, {
          wordIds: [...(wordIds.current ?? [])],
          ignoredWordIds,
        });
      }
    };

    // --- Highlighting Effect ---
    // This effect is responsible for rendering the highlighted text inside the content-editable div.
    useEffect(() => {
      if (isComposing.current) {
        return;
      }
      const currentCursorPos = desiredCursorPos.current;
      const onHighlightedWord = (wordId: ID) =>
        setIgnoredWordIds((prev) => [...prev, wordId]);

      renderHighlightedText(value, {
        inputRef,
        language,
        dateFormat,
        MASTER_REGEX_G,
        wordIds: wordIds.current ?? [],
        ignoredWordIds,
        createTargetHabitAllowed,
        createHabitAllowed,
        autocomplete,
        people,
        desiredCursorPos,
        onHighlightedWord,
        styles,
      });

      // If we have programmatically set the cursor's desired position, we must also restore focus
      // to the input field, as re-rendering the innerHTML can sometimes cause it to be lost.
      if (currentCursorPos !== null) {
        inputRef.current?.focus();
      }
    }, [
      value,
      language,
      dateFormat,
      MASTER_REGEX_G,
      createTargetHabitAllowed,
      createHabitAllowed,
      autocomplete,
      ignoredWordIds,
      wordIds,
      inputRef,
      styles,
      people,
    ]);

    return (
      <Styled.Container>
        <Styled.InputWrapper ref={wrapperRef}>
          {!value.trim() && !isComposingState && (
            <Styled.Placeholder>{placeholder}</Styled.Placeholder>
          )}
          <Styled.RichTextInput
            ref={inputRef}
            contentEditable={true}
            onInput={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            suppressContentEditableWarning={true}
            data-placeholder={placeholder}
            autoCorrect="off"
            // @ts-ignore -- autocomplete off for safari
            autoComplete="off"
          />
          {autocomplete.show && !!autocompleteSuggestions.length && (
            // @ts-ignore
            <PopupMenu
              referenceElement={inputRef}
              position={autoCompletePositions[0]}
              fallbackPositions={autoCompletePositions}
              preventReferenceElementOverlap
              noBackdrop
            >
              <Styled.AutocompleteContainer>
                {autocompleteSuggestions.map((item, index) => {
                  const isCreate = item.extraInfo?.isCreate;
                  const isPremium = item.extraInfo?.isPremium;
                  if (
                    item.type === SuggestionTriggers.LinkedItem &&
                    isCreate &&
                    !createGoalAllowed
                  ) {
                    return null;
                  }
                  const isActive = activeIndex === index;
                  const itemStyle = {
                    ...styles.autocompleteItem,
                    ...(isActive && styles.autocompleteItemActive),
                    ...((isCreate || isPremium) && styles.createItem),
                  };

                  return (
                    // eslint-disable-next-line max-len -- lint
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions -- todo: replace
                    <div
                      key={index}
                      ref={(el) => (suggestionRefs.current[index] = el)}
                      onMouseDown={handleSelectionMouseDown}
                      onClick={() => handleSelection(item)}
                      style={itemStyle}
                    >
                      {item.name}
                    </div>
                  );
                })}
              </Styled.AutocompleteContainer>
            </PopupMenu>
          )}
        </Styled.InputWrapper>
      </Styled.Container>
    );
  },
);
